/// <reference types="@types/google.maps" />
import { useCallback, useRef } from "react";
import { debug as debugFn } from "debug";
import type { ReactGoogleAutocompleteProps } from "react-google-autocomplete";
import { usePlacesWidget } from "react-google-autocomplete";

import type { AdHocPlaceOfSafetyUnbranded, Prettify } from "@thalamos/common";

const debug = debugFn("vision-frontend:useGoogleAutocomplete");

export type AdHocPlaceOfSafetyAddress = Prettify<
  Partial<Pick<AdHocPlaceOfSafetyUnbranded, "address" | "postalCode">>
>;

export const useGoogleAutocomplete = ({
  apiKey,
  onPlaceSelected,
}: Required<Pick<ReactGoogleAutocompleteProps, "apiKey">> & {
  onPlaceSelected: (
    adHocPlaceOfSafetyAddress: AdHocPlaceOfSafetyAddress,
  ) => void;
}) => {
  const handlePlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      debug("Google API provided", place);

      // We want to take the formatted address from the Google API
      // and remove the postal code from it so we can put into another
      // field. We don't compose our own address out of the fields because
      // we have had issues with clients not liking some of the values
      // provided (e.g. { county : "Bournemouth, Christchurch and Poole" }).
      const placeAddress = place.formatted_address;
      const placePostCode = place.address_components?.find((component) =>
        component.types.includes("postal_code"),
      )?.long_name;

      const address = (() => {
        let tempAddress = placeAddress;
        if (placePostCode && tempAddress?.includes(placePostCode)) {
          tempAddress = tempAddress.replace(placePostCode, "").trim();
        }

        return tempAddress;
      })();

      const value = {
        address,
        postalCode: placePostCode,
      };

      debug("Interpreted Google's suggest address as", value);
      onPlaceSelected(value);
    },
    [onPlaceSelected],
  );

  // Make sure we always have access to the most recent
  // version of the callback, which will have new bound
  // values courtesy of the hook dependencies.
  const handlePlaceSelectedRef = useRef(handlePlaceSelected);
  handlePlaceSelectedRef.current = handlePlaceSelected;

  const hookResult = usePlacesWidget({
    apiKey,
    // This usePlacesWidget never stores anything but the initial version
    // of this callback. It doesn't put it in any of it's hook dependencies.
    // To work around this, we use a second level of indirection through a
    // ref hook to ensure we always call with the latest bound dependencies.
    onPlaceSelected: (place) => {
      handlePlaceSelectedRef.current(place);
    },
    options: {
      // TIP: If you set this to `fields: ["ALL"]`, you can see the full set of values which could be returned.
      //      This costs money, so don't leave it on in production.
      // fields: ["ALL"],
      fields: [
        "name",
        "type",
        "address_components",
        "formatted_address",
        "geometry",
      ],
      // This types array needs to be empty in order to be able to lookup
      // both residential and establishment (e.g. hospital) addresses.
      types: [],
      componentRestrictions: { country: ["uk"] },
    },
  });

  return hookResult;
};
